"use client";

import AllUniversities from "@/components/allUniversities/AllUniversities";
import Banner from "@/components/allUniversities/Banner";
import PerfectMatch from "@/components/tools/checkEligibility/PerfectMatches";
import CustomPagination from "@/components/common/CustomPagination";
import Footer from "@/components/common/Footer";
import { useFullPath } from "@/hook/useFullPath";
import { getCountryUniversityList } from "@/store/feature/country/countryApi";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { sortDataType } from "@/store/types/aiCourseFinder";
import { checkCountry, replacedName } from "@/utils/Constants";
import { formatCountryUrl } from "@/utils/formatCountryUrl";
import { getFormattedCountryName } from "@/utils/getFormattedCountryName";
import { useParams, useRouter } from "next/navigation";
import { useEffect, useState, useCallback } from "react";
import { NewNavBar } from "@/components/common/NewNavBar";

const AllUniversitiesHome = () => {
  const router = useRouter();
  const searchParams = useParams();
  const dispatch = useAppDispatch();
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const {
    countryList,
    universityListTotal,
    loading: countryLoading,
  } = useAppSelector((state) => state.country);
  const [sortData, setSortData] = useState<sortDataType>({
    sortingKey: "",
    sortingDirection: "",
  });
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const handleSearch = useCallback((value: string) => {
    setPage(1);
    setDebouncedSearch(value);
  }, []);

  const params = useFullPath();
  // PAGINATION STATES
  const limit = 10;
  const [page, setPage] = useState<number>(
    Number(params.split("page=")[1]) || 1,
  );
  const totalPages = Math.ceil(universityListTotal / limit);

  // COMPARE ID FROM URL PARAM WITH COUNTRY LIST AND GET COUNTRY ID
  const { id } = searchParams;
  const routeId = Array.isArray(id) ? id[0] : (id as string);

  const matchedCountry = countryList.find(
    (item) => formatCountryUrl(item.country_name) === routeId,
  );

  const countryId = matchedCountry?.country_id;

  const res =
    countryList &&
    countryList.find((item) => item.country_id === Number(countryId));
  const country = getFormattedCountryName(
    res?.country_name ?? routeId?.replaceAll("-", " "),
    checkCountry,
    replacedName,
  );
  const isResolvingInitialData =
    countryLoading || (countryList.length === 0 && !!routeId);

  useEffect(() => {
    if (!countryId) return;

    try {
      dispatch(
        getCountryUniversityList({
          countryId: countryId as string | number,
          limit,
          offset: (page - 1) * limit,
          fields:
            "college_id,college_name,address_info,country_id,founded_year,college_slug,rank_details,category,specialisation_category,application_fees,offer_tat,currency",
          courseLevel: selectedCategory,
          sort: sortData,
          collegeName: debouncedSearch,
        }),
      );
    } catch (error) {
      console.error("error", error);
    }
  }, [dispatch, countryId, router, selectedCategory, page, sortData, debouncedSearch]);
  return (
    <div className="bg-[#eee2f5] overflow-hidden">
      <NewNavBar />
      <Banner country={country} />
      <AllUniversities
        country={country}
        setSelectedCategory={setSelectedCategory}
        selectedCategory={selectedCategory}
        setSortData={setSortData}
        setPage={setPage}
        sortData={sortData}
        forceLoading={isResolvingInitialData}
        onSearch={handleSearch}
      />
      {!isResolvingInitialData && totalPages > 1 && (
        <CustomPagination
          totalPages={totalPages}
          onPageChange={(page) => {
            setPage(page);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        />
      )}
      <Footer />
    </div>
  );
};

export default AllUniversitiesHome;
